/**
 * Centralized Analytics Service for GA4 and PostHog (Lureo) tracking.
 *
 * All analytics events go through this service for consistency, centralisation,
 * and dependency injection. PostHog is bound at runtime via setPostHog() from
 * a component that has access to usePostHog().
 */

/** Minimal PostHog-like interface so we don't depend on posthog-js in this module. */
export interface PostHogLike {
  capture(event: string, properties?: Record<string, unknown>): void;
  identify(distinctId: string, properties?: Record<string, unknown>): void;
  /** posthog-js sets this once init has run; a client that was never initialised (no consent, no key) is false. */
  __loaded?: boolean;
}

/** Lureo event names (PostHog / Customer.io). */
export const LUREO_EVENTS = {
  APP_SIGN_UP: 'Lureo_App_Sign_Up',
  APP_LOG_IN: 'Lureo_App_Log_In',
  NEW_PROJECT_CREATED: 'Lureo_New_Project_Created',
  NEW_SCENE_CREATED: 'Lureo_New_Scene_Created',
  ASSET_ADDED: 'Lureo_Asset_Added',
  PUBLISHED_SCENE: 'Lureo_Published_Scene',
  EDITOR_SESSION_DURATION: 'Lureo_Editor_Session_Duration',
  TUTORIAL_VIDEOS_OPENED: 'Lureo_Tutorial_Videos_Opened',
  USER_FEEDBACK_SUBMITTED: 'Lureo_User_Feedback_Submitted',
  TEMPLATE_CREATED: 'Lureo_Template_Created',
  TEMPLATE_SUBMITTED: 'Lureo_Template_Submitted',
  TEMPLATE_OPENED: 'Lureo_Template_Opened',
  TEMPLATE_INTEREST_CLICKED: 'Lureo_Template_Interest_Clicked',
  PUBLISH_FLOW_STEP_VIEWED: 'Lureo_Publish_Flow_Step_Viewed',
  PUBLISH_FLOW_SUBMITTED: 'Lureo_Publish_Flow_Submitted',
  CONNECTION_PHASE_CHANGED: 'Lureo_Connection_Phase_Changed',
  // Phase 3: Scene lifecycle
  SCENE_DELETED: 'Lureo_Scene_Deleted',
  SCENE_UNPUBLISHED: 'Lureo_Scene_Unpublished',
  // Phase 4: Editor behaviour & drop-off
  EDITOR_FIRST_ACTION: 'Lureo_Editor_First_Action',
  TIMELINE_PLAYBACK: 'Lureo_Timeline_Playback',
  ASSET_REMOVED: 'Lureo_Asset_Removed',
  UPLOAD_COMPLETED: 'Lureo_Upload_Completed',
  ASSET_NOT_PROCESSED: 'Lureo_Asset_Not_Processed',
  CLIP_ADDED: 'Lureo_Clip_Added',
  EDITOR_EXITED: 'Lureo_Editor_Exited',
  UNDO_REDO: 'Lureo_Undo_Redo',
  INACTIVITY_TIMEOUT: 'Lureo_Inactivity_Timeout',
  SIDE_MENU_OPENED: 'Lureo_Side_Menu_Opened',
  SCENE_LINK_COPIED: 'Lureo_Scene_Link_Copied',
  // Test-in-mobile-app guidance
  TEST_INSTRUCTIONS_OPENED: 'Lureo_Test_Instructions_Opened',
  PRE_PUBLISH_CHECK_SHOWN: 'Lureo_Pre_Publish_Check_Shown',
  PRE_PUBLISH_CHECK_RESOLVED: 'Lureo_Pre_Publish_Check_Resolved',
  // Tutorial engine
  TUTORIAL_ANCHOR_MISSED: 'Lureo_Tutorial_Anchor_Missed',
  // Guidance (governor-gated contextual nudges)
  GUIDANCE_SHOWN: 'Lureo_Guidance_Shown',
  GUIDANCE_SUPPRESSED: 'Lureo_Guidance_Suppressed',
  GUIDANCE_DISMISSED: 'Lureo_Guidance_Dismissed',
  // Tips (ambient idle, governor-gated)
  TIP_IMPRESSION: 'Lureo_Tip_Impression',
  TIP_DISMISSED: 'Lureo_Tip_Dismissed',
  // Activation funnel (Phase 2 instrumentation blockers)
  KEYFRAME_ADDED: 'Lureo_Keyframe_Added',
  IMAGE_ANCHOR_ADDED: 'Lureo_Image_Anchor_Added',
  TEST_CLICKED: 'Lureo_Test_Clicked',
  SHARE_DIALOG_OPENED: 'Lureo_Share_Dialog_Opened',
  ONBOARDING_INFO_SUBMITTED: 'Lureo_Onboarding_Info_Submitted',
  // Trigger authoring (guided setup); read by the internal test round
  TRIGGER_SETUP_STARTED: 'Lureo_Trigger_Setup_Started',
  TRIGGER_SETUP_STEP_REACHED: 'Lureo_Trigger_Setup_Step_Reached',
  TRIGGER_SETUP_FINISHED: 'Lureo_Trigger_Setup_Finished',
  TRIGGER_SETUP_ABANDONED: 'Lureo_Trigger_Setup_Abandoned',
  // Ball Reveal generator (Forms library)
  BALL_REVEAL_OPENED: 'Lureo_Ball_Reveal_Opened',
  BALL_REVEAL_COMMITTED: 'Lureo_Ball_Reveal_Committed',
  // Banner unfurl generator (FOOH library)
  BANNER_UNFURL_OPENED: 'Lureo_Banner_Unfurl_Opened',
  BANNER_UNFURL_COMMITTED: 'Lureo_Banner_Unfurl_Committed',
} as const;

export type BallRevealCommittedParams = {
  scene_instance_id?: string;
  asset_type_id?: string;
  /** added: a new reveal; placed_again: another placement of a saved one; updated: a saved one replaced in place */
  action: 'added' | 'placed_again' | 'updated';
  ball_count: number;
  shape: string;
  exits: string;
  target_kind: string;
  ending: 'float' | 'spill';
  duration_seconds: number;
  triangles: number;
  warnings: string;
};

export type BannerUnfurlCommittedParams = {
  scene_instance_id?: string;
  asset_type_id?: string;
  action: 'added' | 'placed_again' | 'updated';
  image_source: 'library' | 'upload' | 'none';
  height_m: number;
  width_m: number;
  after: string;
  rods: boolean;
  duration_seconds: number;
  warnings: string;
};

export interface IAnalyticsService {
  /** Bind PostHog instance (called from React when usePostHog() is available). */
  setPostHog(posthog: PostHogLike | null): void;
  /**
   * Identify the user in PostHog by internal user_id only.
   * Non-PII behavioural attributes (persona, commercial_intent, discovery_source) are allowed.
   * Never pass email, name, or other direct PII — Customer.io receives that
   * data directly from the backend API.
   */
  identify(userId: string, properties?: {
    persona?: string | null;
    commercial_intent?: string | null;
    discovery_source?: string | null;
    track_one_member?: boolean | null;
    track_two_member?: boolean | null;
  }): void;
  /**
   * Set person properties on the current PostHog person (issues a $set). Forwarded to
   * Customer.io as profile attributes by the PostHog destination. Used for signup
   * attribution (signup_utm_*). Never pass PII.
   */
  setPersonProperties(properties: Record<string, unknown>): void;
  trackAssetUpload(params: AssetUploadParams): void;
  trackEditorSession(params: EditorSessionParams): void;
  /** Send a generic event to GA4 (gtag). For PostHog use trackLureoEvent or the typed trackLureo* methods. */
  trackEvent(eventName: string, params?: Record<string, unknown>): void;
  // Lureo / PostHog events (Customer.io workflows)
  trackLureoAppSignUp(params?: { signup_method?: string } & Record<string, unknown>): void;
  trackLureoAppLogIn(params?: { login_method?: string }): void;
  trackLureoNewProjectCreated(params?: { project_id?: string }): void;
  trackLureoNewSceneCreated(params?: {
    project_id?: string;
    scene_instance_id?: string;
    from_template?: boolean;
    template_scene_instance_id?: string;
    template_creator_user_id?: string;
  }): void;
  trackLureoAssetAdded(params?: {
    scene_instance_id?: string;
    asset_type_id?: string;
    asset_type_source_name?: string;
    asset_category?: string;
    asset_name?: string;
  }): void;
  /** A keyframe the user explicitly created (not auto-created defaults). */
  trackLureoKeyframeAdded(params: { scene_instance_id?: string; asset_instance_id?: string }): void;
  /** An image anchor was added to the scene. */
  trackLureoImageAnchorAdded(params: {
    scene_instance_id?: string;
    asset_instance_id?: string;
    asset_instance_source_name?: string;
    asset_category?: string;
  }): void;
  /** The Test button was clicked (last web step before the AR payoff). */
  trackLureoTestClicked(params: { scene_instance_id?: string; source?: string }): void;
  /** A share/test dialog opened, broken down by target. */
  trackLureoShareDialogOpened(params: {
    scene_instance_id?: string;
    project_instance_id?: string;
    target?: 'phone' | 'appless' | 'spectacles';
  }): void;
  trackLureoPublishedScene(params?: {
    scene_instance_id?: string;
    publish_type?: 'first_publish' | 're_publish';
    scene_version?: number;
    project_id?: string;
  }): void;
  trackLureoTutorialVideosOpened(params?: { trigger_context?: string }): void;
  /** User experience feedback submitted (rating 1–5, source, optional has_response). */
  trackLureoUserFeedbackSubmitted(params: {
    rating: number;
    source: string;
    has_response?: boolean;
  }): void;
  /**
   * Onboarding questions answered, including where the user heard about Lureo.
   * The *_other verbatims are sanitized event properties: unlike person
   * properties they stay on this one event and are not forwarded to Customer.io.
   */
  trackLureoOnboardingInfoSubmitted(params: {
    role_category: string;
    business_use: string;
    discovery_source: string;
    role_other_text?: string;
    discovery_source_other?: string;
  }): void;
  /** Editor session: enter = user entered editor; exit = session ended (with duration). */
  trackLureoEditorSessionDuration(params: {
    action: 'enter' | 'exit';
    duration_seconds?: number;
    scene_instance_id?: string;
  }): void;
  /** Capture any Lureo_* event to PostHog (for one-off events not on the typed interface). */
  trackLureoEvent(eventName: string, params?: Record<string, unknown>): void;

  trackLureoTriggerSetupStarted(params: { scene_instance_id?: string; asset_instance_id?: string }): void;
  trackLureoBallRevealOpened(params: { scene_instance_id?: string; saved: boolean }): void;
  trackLureoBallRevealCommitted(params: BallRevealCommittedParams): void;
  trackLureoBannerUnfurlOpened(params: { scene_instance_id?: string; saved: boolean }): void;
  trackLureoBannerUnfurlCommitted(params: BannerUnfurlCommittedParams): void;
  trackLureoTriggerSetupStepReached(params: { step: number; trigger_type?: string | null }): void;
  trackLureoTriggerSetupFinished(params: {
    trigger_type?: string | null;
    wait_mode?: 'hold' | 'animation' | 'hidden' | null;
    elapsed_seconds?: number;
    scene_instance_id?: string;
  }): void;
  trackLureoTriggerSetupAbandoned(params: {
    step: number;
    trigger_type?: string | null;
    elapsed_seconds?: number;
  }): void;
  /** Admin flipped an existing scene version to template status. */
  trackLureoTemplateCreated(params: { template_scene_instance_id: string }): void;
  /** User submitted a template for review. */
  trackLureoTemplateSubmitted(params: {
    template_scene_instance_id: string;
    template_submission_id?: string;
    is_resubmission?: boolean;
    should_be_paid?: boolean;
  }): void;
  /** Non-eligible user clicked through to the paid-templates interest form. */
  trackLureoTemplateInterestClicked(params: { scene_instance_id?: string }): void;
  /** A publish flow step came into view. `destination` separates the audience and template paths. */
  trackLureoPublishFlowStepViewed(params: {
    destination: 'audience' | 'template';
    step: string;
    step_index: number;
    step_count: number;
    scene_instance_id?: string;
  }): void;
  /** The publish flow's final Submit was pressed, before the write runs. */
  trackLureoPublishFlowSubmitted(params: {
    destination: 'audience' | 'template';
    devices?: string[];
    scene_instance_id?: string;
  }): void;
  /** User opened a template from Template Stories (includes original template and creator). */
  trackLureoTemplateOpened(params: {
    original_template_scene_instance_id: string;
    template_creator_user_id: string;
  }): void;
  /** User pressed the reload connection button after being away. */
  trackLureoConnectionPhaseChanged(params: {
    scene_instance_id?: string;
    from_phase: string;
    to_phase: string;
    from_phase_visible: boolean;
    from_phase_duration_ms: number;
    network_band: string;
    service_band: string;
    network_samples_ms: (number | null)[];
    service_samples_ms: (number | null)[];
  }): void;
  // Phase 3: Scene lifecycle
  trackLureoSceneDeleted(params: { scene_instance_id: string; project_id?: string }): void;
  trackLureoSceneUnpublished(params: { scene_instance_id?: string; project_id?: string; scene_version?: number }): void;
  // Phase 4: Editor behaviour & drop-off
  trackLureoEditorFirstAction(params: { scene_instance_id?: string; action_type?: string; time_to_first_action_seconds?: number }): void;
  trackLureoTimelinePlayback(params: { scene_instance_id?: string; action: 'play' | 'pause' | 'reset' }): void;
  trackLureoAssetRemoved(params: { scene_instance_id?: string; asset_category?: string; asset_name?: string }): void;
  trackLureoUploadCompleted(params: { scene_instance_id?: string; entry_point?: string; asset_category?: string; file_type?: string; success: boolean; error?: string }): void;
  trackLureoAssetNotProcessed(params: { scene_instance_id?: string; asset_type_id?: string; asset_name?: string; asset_category?: string }): void;
  trackLureoClipAdded(params: { scene_instance_id?: string; clip_category?: string; clip_source?: string; asset_type_id?: string }): void;
  trackLureoEditorExited(params: { scene_instance_id?: string; exit_method?: string; gave_feedback?: boolean; assets_in_scene?: number; has_published?: boolean }): void;
  trackLureoUndoRedo(params: { scene_instance_id?: string; action: 'undo' | 'redo' }): void;
  trackLureoInactivityTimeout(params: { scene_instance_id?: string; time_before_timeout_seconds?: number }): void;
  trackLureoSideMenuOpened(params: { scene_instance_id?: string; menu_name: string }): void;
  trackLureoSceneLinkCopied(params: { scene_instance_id?: string; link_type?: string; project_id?: string }): void;
  trackLureoTestInstructionsOpened(params: {
    source: 'projects_page' | 'story_edit_page' | 'pre_publish_check' | 'editor_qr';
    project_id?: string;
    scene_instance_id?: string;
  }): void;
  trackLureoPrePublishCheckShown(params: {
    surface: 'story_edit_page' | 'projects_page';
    noun: 'story' | 'project';
    project_id?: string;
    scene_instance_id?: string;
  }): void;
  trackLureoPrePublishCheckResolved(params: {
    surface: 'story_edit_page' | 'projects_page';
    choice: 'view_instructions' | 'proceed_to_publish' | 'cancel';
    project_id?: string;
    scene_instance_id?: string;
  }): void;
}

export interface AssetUploadParams {
  userId: string;
  assetName: string;
  assetCategory: string;
  fileName: string;
}

export interface EditorSessionParams {
  sessionDurationSeconds: number;
  sceneInstanceId?: string;
  userId?: string;
  /** If true, use beacon transport for reliable delivery on page close */
  useBeacon?: boolean;
}

export class AnalyticsService implements IAnalyticsService {
  private posthog: PostHogLike | null = null;

  /** The client, once PostHog has initialised; calls to one that hasn't only log warnings. */
  private get client(): PostHogLike | null {
    return this.posthog && this.posthog.__loaded !== false ? this.posthog : null;
  }

  setPostHog(posthog: PostHogLike | null): void {
    this.posthog = posthog;
  }

  identify(userId: string, properties?: {
    persona?: string | null;
    commercial_intent?: string | null;
    discovery_source?: string | null;
    track_one_member?: boolean | null;
    track_two_member?: boolean | null;
  }): void {
    this.client?.identify(userId, properties as Record<string, unknown>);
  }

  setPersonProperties(properties: Record<string, unknown>): void {
    if (!properties || Object.keys(properties).length === 0) return;
    // posthog.setPersonProperties() under the hood; the $set event is what the
    // PostHog -> Customer.io destination forwards as profile attributes.
    this.client?.capture('$set', { $set: properties });
  }

  trackLureoAppSignUp(params?: { signup_method?: string } & Record<string, unknown>): void {
    this.client?.capture(LUREO_EVENTS.APP_SIGN_UP, params ?? {});
  }

  trackLureoAppLogIn(params?: { login_method?: string }): void {
    this.client?.capture(LUREO_EVENTS.APP_LOG_IN, params ?? {});
  }

  trackLureoNewProjectCreated(params?: { project_id?: string }): void {
    this.client?.capture(LUREO_EVENTS.NEW_PROJECT_CREATED, params ?? {});
  }

  trackLureoNewSceneCreated(params?: {
    project_id?: string;
    scene_instance_id?: string;
    from_template?: boolean;
    template_scene_instance_id?: string;
    template_creator_user_id?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.NEW_SCENE_CREATED, params ?? {});
  }

  trackLureoAssetAdded(params?: {
    scene_instance_id?: string;
    asset_type_id?: string;
    asset_type_source_name?: string;
    asset_category?: string;
    asset_name?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.ASSET_ADDED, params ?? {});
  }

  trackLureoKeyframeAdded(params: { scene_instance_id?: string; asset_instance_id?: string }): void {
    this.client?.capture(LUREO_EVENTS.KEYFRAME_ADDED, params);
  }

  trackLureoImageAnchorAdded(params: {
    scene_instance_id?: string;
    asset_instance_id?: string;
    asset_instance_source_name?: string;
    asset_category?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.IMAGE_ANCHOR_ADDED, params);
  }

  trackLureoTestClicked(params: { scene_instance_id?: string; source?: string }): void {
    this.client?.capture(LUREO_EVENTS.TEST_CLICKED, params);
  }

  trackLureoShareDialogOpened(params: {
    scene_instance_id?: string;
    project_instance_id?: string;
    target?: 'phone' | 'appless' | 'spectacles';
  }): void {
    this.client?.capture(LUREO_EVENTS.SHARE_DIALOG_OPENED, params);
  }

  trackLureoPublishedScene(params?: {
    scene_instance_id?: string;
    publish_type?: 'first_publish' | 're_publish';
    scene_version?: number;
    project_id?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.PUBLISHED_SCENE, params ?? {});
  }

  trackLureoTutorialVideosOpened(params?: { trigger_context?: string }): void {
    this.client?.capture(LUREO_EVENTS.TUTORIAL_VIDEOS_OPENED, params ?? {});
  }

  trackLureoUserFeedbackSubmitted(params: {
    rating: number;
    source: string;
    has_response?: boolean;
  }): void {
    this.client?.capture(LUREO_EVENTS.USER_FEEDBACK_SUBMITTED, params);
  }

  trackLureoOnboardingInfoSubmitted(params: {
    role_category: string;
    business_use: string;
    discovery_source: string;
    role_other_text?: string;
    discovery_source_other?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.ONBOARDING_INFO_SUBMITTED, params);
  }

  trackLureoEditorSessionDuration(params: {
    action: 'enter' | 'exit';
    duration_seconds?: number;
    scene_instance_id?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.EDITOR_SESSION_DURATION, params);
  }

  trackLureoEvent(eventName: string, params?: Record<string, unknown>): void {
    this.client?.capture(eventName, params ?? {});
  }

  trackLureoBallRevealOpened(params: { scene_instance_id?: string; saved: boolean }): void {
    this.client?.capture(LUREO_EVENTS.BALL_REVEAL_OPENED, params);
  }

  trackLureoBallRevealCommitted(params: BallRevealCommittedParams): void {
    this.client?.capture(LUREO_EVENTS.BALL_REVEAL_COMMITTED, params);
  }

  trackLureoBannerUnfurlOpened(params: { scene_instance_id?: string; saved: boolean }): void {
    this.client?.capture(LUREO_EVENTS.BANNER_UNFURL_OPENED, params);
  }

  trackLureoBannerUnfurlCommitted(params: BannerUnfurlCommittedParams): void {
    this.client?.capture(LUREO_EVENTS.BANNER_UNFURL_COMMITTED, params);
  }

  trackLureoTriggerSetupStarted(params: { scene_instance_id?: string; asset_instance_id?: string }): void {
    this.client?.capture(LUREO_EVENTS.TRIGGER_SETUP_STARTED, params);
  }

  trackLureoTriggerSetupStepReached(params: { step: number; trigger_type?: string | null }): void {
    this.client?.capture(LUREO_EVENTS.TRIGGER_SETUP_STEP_REACHED, params);
  }

  trackLureoTriggerSetupFinished(params: {
    trigger_type?: string | null;
    wait_mode?: 'hold' | 'animation' | 'hidden' | null;
    elapsed_seconds?: number;
    scene_instance_id?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.TRIGGER_SETUP_FINISHED, params);
  }

  trackLureoTriggerSetupAbandoned(params: {
    step: number;
    trigger_type?: string | null;
    elapsed_seconds?: number;
  }): void {
    this.client?.capture(LUREO_EVENTS.TRIGGER_SETUP_ABANDONED, params);
  }

  trackLureoTemplateCreated(params: { template_scene_instance_id: string }): void {
    this.client?.capture(LUREO_EVENTS.TEMPLATE_CREATED, params);
  }

  trackLureoTemplateSubmitted(params: {
    template_scene_instance_id: string;
    template_submission_id?: string;
    is_resubmission?: boolean;
    should_be_paid?: boolean;
  }): void {
    this.client?.capture(LUREO_EVENTS.TEMPLATE_SUBMITTED, params);
  }

  trackLureoTemplateInterestClicked(params: { scene_instance_id?: string }): void {
    this.client?.capture(LUREO_EVENTS.TEMPLATE_INTEREST_CLICKED, params);
  }

  trackLureoPublishFlowStepViewed(params: {
    destination: 'audience' | 'template';
    step: string;
    step_index: number;
    step_count: number;
    scene_instance_id?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.PUBLISH_FLOW_STEP_VIEWED, params);
  }

  trackLureoPublishFlowSubmitted(params: {
    destination: 'audience' | 'template';
    devices?: string[];
    scene_instance_id?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.PUBLISH_FLOW_SUBMITTED, params);
  }

  trackLureoTemplateOpened(params: {
    original_template_scene_instance_id: string;
    template_creator_user_id: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.TEMPLATE_OPENED, params);
  }


  trackLureoConnectionPhaseChanged(params: {
    scene_instance_id?: string;
    from_phase: string;
    to_phase: string;
    from_phase_visible: boolean;
    from_phase_duration_ms: number;
    network_band: string;
    service_band: string;
    network_samples_ms: (number | null)[];
    service_samples_ms: (number | null)[];
  }): void {
    this.client?.capture(LUREO_EVENTS.CONNECTION_PHASE_CHANGED, params);
  }

  // Phase 3: Scene lifecycle

  trackLureoSceneDeleted(params: { scene_instance_id: string; project_id?: string }): void {
    this.client?.capture(LUREO_EVENTS.SCENE_DELETED, params);
  }

  trackLureoSceneUnpublished(params: { scene_instance_id?: string; project_id?: string; scene_version?: number }): void {
    this.client?.capture(LUREO_EVENTS.SCENE_UNPUBLISHED, params ?? {});
  }

  // Phase 4: Editor behaviour & drop-off

  trackLureoEditorFirstAction(params: { scene_instance_id?: string; action_type?: string; time_to_first_action_seconds?: number }): void {
    this.client?.capture(LUREO_EVENTS.EDITOR_FIRST_ACTION, params);
  }

  trackLureoTimelinePlayback(params: { scene_instance_id?: string; action: 'play' | 'pause' | 'reset' }): void {
    this.client?.capture(LUREO_EVENTS.TIMELINE_PLAYBACK, params);
  }

  trackLureoAssetRemoved(params: { scene_instance_id?: string; asset_category?: string; asset_name?: string }): void {
    this.client?.capture(LUREO_EVENTS.ASSET_REMOVED, params);
  }

  trackLureoUploadCompleted(params: { scene_instance_id?: string; entry_point?: string; asset_category?: string; file_type?: string; success: boolean; error?: string }): void {
    this.client?.capture(LUREO_EVENTS.UPLOAD_COMPLETED, params);
  }

  trackLureoAssetNotProcessed(params: { scene_instance_id?: string; asset_type_id?: string; asset_name?: string; asset_category?: string }): void {
    this.client?.capture(LUREO_EVENTS.ASSET_NOT_PROCESSED, params);
  }

  trackLureoClipAdded(params: { scene_instance_id?: string; clip_category?: string; clip_source?: string; asset_type_id?: string }): void {
    this.client?.capture(LUREO_EVENTS.CLIP_ADDED, params);
  }

  trackLureoEditorExited(params: { scene_instance_id?: string; exit_method?: string; gave_feedback?: boolean; assets_in_scene?: number; has_published?: boolean }): void {
    this.client?.capture(LUREO_EVENTS.EDITOR_EXITED, params);
  }

  trackLureoUndoRedo(params: { scene_instance_id?: string; action: 'undo' | 'redo' }): void {
    this.client?.capture(LUREO_EVENTS.UNDO_REDO, params);
  }

  trackLureoInactivityTimeout(params: { scene_instance_id?: string; time_before_timeout_seconds?: number }): void {
    this.client?.capture(LUREO_EVENTS.INACTIVITY_TIMEOUT, params);
  }

  trackLureoSideMenuOpened(params: { scene_instance_id?: string; menu_name: string }): void {
    this.client?.capture(LUREO_EVENTS.SIDE_MENU_OPENED, params);
  }

  trackLureoSceneLinkCopied(params: { scene_instance_id?: string; link_type?: string; project_id?: string }): void {
    this.client?.capture(LUREO_EVENTS.SCENE_LINK_COPIED, params);
  }

  trackLureoTestInstructionsOpened(params: {
    source: 'projects_page' | 'story_edit_page' | 'pre_publish_check' | 'editor_qr';
    project_id?: string;
    scene_instance_id?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.TEST_INSTRUCTIONS_OPENED, params);
  }

  trackLureoPrePublishCheckShown(params: {
    surface: 'story_edit_page' | 'projects_page';
    noun: 'story' | 'project';
    project_id?: string;
    scene_instance_id?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.PRE_PUBLISH_CHECK_SHOWN, params);
  }

  trackLureoPrePublishCheckResolved(params: {
    surface: 'story_edit_page' | 'projects_page';
    choice: 'view_instructions' | 'proceed_to_publish' | 'cancel';
    project_id?: string;
    scene_instance_id?: string;
  }): void {
    this.client?.capture(LUREO_EVENTS.PRE_PUBLISH_CHECK_RESOLVED, params);
  }

  private isGtagAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  }

  /**
   * Track an asset upload event
   */
  trackAssetUpload(params: AssetUploadParams): void {
    if (!this.isGtagAvailable()) return;

    const fileExtension = params.fileName.split('.').pop()?.toLowerCase() || 'unknown';

    window.gtag('event', 'asset_uploaded', {
      user_id: params.userId,
      asset_name: params.assetName,
      asset_category: params.assetCategory,
      file_type: fileExtension
    });

    console.log('Analytics: asset_uploaded', params.assetName);
  }

  /**
   * Track an editor session duration
   */
  trackEditorSession(params: EditorSessionParams): void {
    if (!this.isGtagAvailable()) return;

    const eventParams: Record<string, unknown> = {
      session_duration_seconds: params.sessionDurationSeconds
    };

    if (params.sceneInstanceId) {
      eventParams.scene_instance_id = params.sceneInstanceId;
    }

    if (params.userId) {
      eventParams.user_id = params.userId;
    }

    if (params.useBeacon) {
      eventParams.transport_type = 'beacon';
    }

    window.gtag('event', 'editor_session', eventParams);

    console.log('Analytics: editor_session', params.sessionDurationSeconds + 's');
  }

  /**
   * Track a generic event to GA4 (gtag). For PostHog use trackLureoEvent or typed methods.
   */
  trackEvent(eventName: string, params?: Record<string, unknown>): void {
    if (!this.isGtagAvailable()) return;

    window.gtag('event', eventName, params);

    console.log('Analytics:', eventName);
  }
}
